import React, { useState } from 'react';
import { ApiBaseData, ApiConfigurationDataResponse, ApiItemType } from '@ibe/api';
import useApi from '../../../Hooks/useApi';
import { FormGroup } from 'reactstrap';
import AsyncSelect from 'react-select/async';
import fallback from '../../../../_Translations/generated/post-code-selection.en.t.json';
import Keys from '../../../../_Translations/generated/post-code-selection.en.t.json.keys';
import BestPriceAvailabilitySearchStore from '../../../SubModules/BestPriceAvailabilitySearch/BestPriceAvailabilitySearchStore';
import useTranslation from '../../../Hooks/useTranslation';

interface PostCodeSelectionProps {
  ownArrivalsAndDepartures?: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedPostCode: React.Dispatch<React.SetStateAction<ApiBaseData | undefined>>;
  store: BestPriceAvailabilitySearchStore;
}

const PostCodeSelection = (props: PostCodeSelectionProps) => {
  const { store, setSelectedPostCode, setIsOpen, ownArrivalsAndDepartures } = props;
  const [allPostCodes, setAllPostCodes] = useState<ApiBaseData[]>([]);
  const [invalidPostCode, setInvalidPostCode] = useState<boolean>(false);

  const { t } = useTranslation('post-code-selection', fallback, Keys);

  const setPostalCode = (code: string) => {
    if (code && code.length == 5) {
      store.setPostalCode(code);
      const selected = allPostCodes.find(pc => pc.code === code);
      if (selected) {
        setSelectedPostCode(selected);
      }
      store.setSelectedPostalData(selected);
      setIsOpen(true);
    }
  };

  const isNotDigit = (inputValue: string) => {
    if (inputValue === '') return false;
    return !/^\d+$/.test(inputValue);
  };

  interface OptionType {
    value: string;
    label: string;
  }

  const api = useApi();

  const fetchPostCodeOptions = async (inputValue: string): Promise<OptionType[]> => {
    setInvalidPostCode(isNotDigit(inputValue));
    if (!inputValue || inputValue.length < 3) return [];
    try {
      const options: ApiConfigurationDataResponse = await api.getConfiguration({
        itemType: ApiItemType.POSTALCODE
      });
      const all = options.data[ApiItemType.POSTALCODE].filter(
        pc => pc.latitude !== null && pc.longitude !== null
      );
      setAllPostCodes(all);
      return all
        .filter(option => option.code.startsWith(inputValue))
        .map(option => ({ value: option.code, label: option.code }));
    } catch (error) {
      console.error('Error fetching postal codes:', error);
      return [];
    }
  };

  return (
    <label className="custom-fieldset-label">
      <FormGroup className="required">
        <AsyncSelect
          className={`post-code-select ${invalidPostCode ? 'invalid-post-code' : ''}`}
          styles={{
            control: (provided: object, state: { isFocused: boolean }) => ({
              ...provided,
              boxShadow: state.isFocused ? 'none' : 'none'
            })
          }}
          cacheOptions
          defaultOptions
          isDisabled={ownArrivalsAndDepartures}
          loadOptions={fetchPostCodeOptions}
          onChange={(e: { value: string } | null): void => {
            if (e) {
              setPostalCode(e.value);
            }
          }}
          placeholder={ownArrivalsAndDepartures ? '' : t(Keys.enterPostalCode)}
          noOptionsMessage={() => t(Keys.noOptions)}
          loadingMessage={() => t(Keys.loading)}
        />
      </FormGroup>
      <span className="placeholder">{t(Keys.plz)}</span>
    </label>
  );
};

export default PostCodeSelection;
